<template>
  <div>
    <div v-if="loading">
      <div class="px-4 py-2 flex items-center gap-3 border-b border-gray-100">
        <div class="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
        <div class="h-3 w-24 bg-gray-100 rounded animate-pulse"></div>
      </div>
      <div class="divide-y divide-gray-100">
        <div v-for="n in 5" :key="'sk-' + n" class="px-4 py-3" :style="{ borderLeft: n % 3 === 0 ? '3px solid #FDE68A' : '3px solid transparent' }">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-1.5">
              <div class="h-3 w-12 bg-gray-200 rounded animate-pulse"></div>
              <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: [120, 160, 100, 140, 130][n - 1] + 'px' }"></div>
            </div>
            <div class="h-3 w-10 bg-gray-100 rounded animate-pulse ml-3"></div>
          </div>
          <div class="flex items-center gap-1.5 mt-1.5">
            <div v-for="y in 3" :key="'sk-yr-' + y" class="h-4 w-10 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="hccs.length === 0" class="text-center py-8 text-gray-500 text-xs">
      No HCC history found.
    </div>
    <div v-else>
      <!-- Toolbar -->
      <div class="px-4 py-2 flex items-center gap-3 border-b border-gray-100">
        <select v-model="filter" class="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white" style="color: #353537;">
          <option value="all">All HCCs</option>
          <option value="gaps">Gaps Only</option>
          <option value="billed">Billed Only</option>
          <option value="chronic">Chronic Only</option>
        </select>
        <span class="text-[10px]" style="color: #A5A5A6;">
          {{ filteredHccs.length }} of {{ hccs.length }} HCCs
          <template v-if="gapCount > 0"> · {{ gapCount }} gaps</template>
        </span>
      </div>

      <!-- HCC Cards -->
      <div class="divide-y divide-gray-100">
        <div
          v-for="hcc in filteredHccs"
          :key="hcc.hcc_code"
          class="px-4 py-3"
          :style="{ borderLeft: hcc.is_gap ? '3px solid #F59E0B' : '3px solid transparent' }"
        >
          <!-- Row 1: HCC code + description + RAF -->
          <div class="flex items-start justify-between">
            <div>
              <span class="text-xs font-semibold" style="color: #353537;">{{ hcc.hcc_code }}</span>
              <span class="text-xs ml-1.5" style="color: #626264;">{{ hcc.description }}</span>
            </div>
            <span class="text-xs font-mono whitespace-nowrap ml-3" style="color: #353537;">{{ hcc.raf_coefficient?.toFixed(3) }}</span>
          </div>

          <!-- Row 2: Year badges + chronic -->
          <div class="flex items-center gap-1.5 mt-1.5">
            <template v-for="year in yearList" :key="year">
              <span
                v-if="hcc.years[year]"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                :class="hcc.years[year].billed
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-50 text-gray-400'"
              >
                {{ hcc.years[year].billed ? '●' : '○' }} {{ year }}
              </span>
            </template>
            <span v-if="hcc.chronic" class="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">
              Chronic
            </span>
          </div>

          <!-- Expandable: Billed ICD codes -->
          <div v-if="hasBilledIcds(hcc)" class="mt-2">
            <button
              @click="toggle(hcc.hcc_code, 'billed')"
              class="text-[10px] font-medium flex items-center gap-1 cursor-pointer"
              style="color: #7799C2;"
            >
              <span>{{ isExpanded(hcc.hcc_code, 'billed') ? '▼' : '▶' }}</span>
              ICD Codes Billed ({{ countBilledIcds(hcc) }})
            </button>
            <div v-if="isExpanded(hcc.hcc_code, 'billed')" class="mt-1 ml-3 space-y-0.5">
              <div
                v-for="icd in hcc.billed_icds"
                :key="icd.code"
                class="flex items-center gap-2 text-[10px]"
              >
                <span class="font-mono font-medium" style="color: #353537;">{{ icd.code }}</span>
                <span v-if="icd.description" style="color: #626264;">{{ icd.description }}</span>
                <span v-if="icd.date" style="color: #A5A5A6;">{{ icd.date }}</span>
              </div>
            </div>
          </div>

          <!-- QA Accepted Codes -->
          <div v-if="hcc.qa_accepted_codes?.length" class="mt-1.5">
            <button
              @click="toggle(hcc.hcc_code, 'qa')"
              class="text-[10px] font-medium flex items-center gap-1 cursor-pointer"
              style="color: #389A6C;"
            >
              <span>{{ isExpanded(hcc.hcc_code, 'qa') ? '▼' : '▶' }}</span>
              QA Accepted ({{ hcc.qa_accepted_codes.length }})
            </button>
            <div v-if="isExpanded(hcc.hcc_code, 'qa')" class="mt-1 ml-3 space-y-0.5">
              <div
                v-for="(qa, idx) in hcc.qa_accepted_codes"
                :key="idx"
                class="flex items-center gap-2 text-[10px]"
              >
                <span class="font-mono font-medium" style="color: #389A6C;">{{ qa.code }}</span>
                <span v-if="qa.description" style="color: #626264;">{{ qa.description }}</span>
                <span v-if="qa.coder" style="color: #A5A5A6;">{{ qa.coder }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Gap summary -->
      <div v-if="gapCount > 0" class="mx-4 mb-3 p-2 bg-amber-50 border border-amber-200 rounded-md text-[10px] text-amber-800">
        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
        {{ gapCount }} recapture gap{{ gapCount > 1 ? 's' : '' }} — billed previously, not yet captured this year.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_HCC_History',
  components: { FontAwesomeIcon },
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      hccs: [],
      yearList: [],
      loading: false,
      filter: 'all',
      expanded: {},  // { 'HCC 37:billed': true, 'HCC 37:mapping': false }
    };
  },
  computed: {
    filteredHccs() {
      switch (this.filter) {
        case 'gaps': return this.hccs.filter(h => h.is_gap);
        case 'billed': return this.hccs.filter(h => !h.is_gap);
        case 'chronic': return this.hccs.filter(h => h.chronic);
        default: return this.hccs;
      }
    },
    gapCount() {
      return this.hccs.filter(h => h.is_gap).length;
    },
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler() {
        const signal = this._renewAbortSignal();
        this.loadData(signal);
      },
    },
  },
  methods: {
    async loadData(signal) {
      if (!this.patientIdentifier) return;
      this.loading = true;
      this.expanded = {};
      try {
        const response = await axios.get(
          `/api/coding/${this.patientIdentifier}/hcc-history/`,
          { signal },
        );
        this.hccs = response.data.hccs || [];
        this.yearList = (response.data.year_list || []).map(String);
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Failed to load HCC history:', error);
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },
    hasBilledIcds(hcc) {
      return hcc.billed_icds?.length > 0;
    },
    countBilledIcds(hcc) {
      return hcc.billed_icds?.length || 0;
    },
    toggle(hccCode, section) {
      const key = `${hccCode}:${section}`;
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },
    isExpanded(hccCode, section) {
      return !!this.expanded[`${hccCode}:${section}`];
    },
  },
};
</script>
